import React from "react";
import { PlusMinus } from "@/components/PlusMinus";
import { PriceDisplay } from "@/features/product/PriceDisplay";
import { useCartUiActions } from "./cartStore";
import {
  cartItemSelect,
  shopifyCartApi,
  ShopifyCartItem,
} from "@/services/shopifyCart";
import { ProductCard } from "../product/ProductCard";
import { SizedImage } from "@/components/SizedImage";
import { giftWithPurchase } from "@/helpers/data/cartGiftProductsData";
import { Money, $ } from "@/helpers/Money";
import { DroppsProduct, productSelect } from "@/store";
import { Page } from "./CartDrawer";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  getEffectiveSubscriptionDiscountPercent,
  getFreeShippingBundleProductIds,
} from "@/helpers/constants";
import { useGlobalMetafields } from "@/services/globalMetafields";

interface CartProductProps {
  item: ShopifyCartItem & { shopifyProduct?: DroppsProduct };
  setOpenDrawer: (page: Page) => void;
}

const subscriptionText = (item: ShopifyCartItem) => {
  const subscription = cartItemSelect.subscription(item);
  return subscription.kind === "selling-plan"
    ? subscription.name
    : "One-Time Purchase";
};

export const CartProduct = ({ item, setOpenDrawer }: CartProductProps) => {
  const { closeDrawer } = useCartUiActions();
  const cartUpdater = shopifyCartApi.useChange();
  const cartUpdate = shopifyCartApi.useUpdate();
  const shopifyCart = shopifyCartApi.useFetch();
  const cart = shopifyCart.data;
  const { data: globalMetafields } = useGlobalMetafields();

  // Check if this item is a gift with purchase (GWP) item
  const isGwpItem = item.properties?.["_gwp"] == "true";

  // Get free shipping bundle product IDs from global metafield
  const bundleProductIds = getFreeShippingBundleProductIds(globalMetafields);

  // Helper function to handle bundle item removal
  const handleBundleRemoval = async (itemToRemove: ShopifyCartItem) => {
    if (!cart) {
      return cartUpdater.mutateAsync({ id: itemToRemove.key, quantity: 0 });
    }

    // Find all other bundle items in cart
    const otherBundleItems = cart.items.filter(
      (cartItem) =>
        bundleProductIds.includes(cartItem.product_id) &&
        cartItem.product_id !== itemToRemove.product_id
    );

    if (otherBundleItems.length > 0) {
      // Remove all bundle items in a single API call
      const updates = otherBundleItems.reduce(
        (acc, cartItem) => {
          acc[cartItem.key] = 0;
          return acc;
        },
        { [itemToRemove.key]: 0 } as Record<string, number>
      );

      return cartUpdate.mutateAsync(updates);
    } else {
      // No other bundle items, just remove this one
      return cartUpdater.mutateAsync({ id: itemToRemove.key, quantity: 0 });
    }
  };

  const getStandardPrice = () => {
    const defaultVariant = item?.shopifyProduct?.variants.find(
      (variant) => variant.default_variant_multiplier === 1
    );

    const selectedVariant =
      item.shopifyProduct &&
      productSelect.selectedVariant(item.shopifyProduct, item.variant_id);
    if (selectedVariant) {
      return selectedVariant.price.mul(item.quantity);
    }
    // fallback if no variant data
    return item.price.mul(item.quantity);
  };

  const productPageUrl = cartItemSelect.productUrl(item);
  const { active: gwpActive, products: gwpProducts } = giftWithPurchase;
  const promoIds = gwpProducts.map((product) => product.variantId);
  const isPromoGift = promoIds.includes(item.variant_id);
  const limitPerOrder = gwpActive && isPromoGift;
  const activePromo = limitPerOrder && item.quantity <= 1;
  const cartValue = cart?.items_subtotal_price ?? Money.zero;

  const baseStandardPrice = getStandardPrice();

  const selectedVariant =
    item.shopifyProduct &&
    productSelect.selectedVariant(item.shopifyProduct, item.variant_id);

  const sellingPlanAllocation = item?.selling_plan_allocation;
  const compareAtPriceCents = sellingPlanAllocation?.compare_at_price;
  const discountedPriceCents = item?.discounted_price;

  const compareAtLinePrice =
    compareAtPriceCents != null
      ? Money.from(compareAtPriceCents, "¢").mul(item.quantity)
      : null;
  const discountedPrice =
    discountedPriceCents != null
      ? Money.from(discountedPriceCents.asCents(), "¢").mul(item.quantity)
      : null;

  const hasCompareAtDiscount =
    compareAtLinePrice &&
    item.final_line_price.asCents() < compareAtLinePrice.asCents();
  const hasCustomDiscount =
    discountedPrice &&
    discountedPrice.asCents() < item.original_line_price.asCents();

  const hasDiscount = !!(hasCompareAtDiscount || hasCustomDiscount);

  const displayStandardPrice =
    hasDiscount && compareAtLinePrice ? compareAtLinePrice : baseStandardPrice;

  const cartItem = {
    title: item.product_title,
    featuredImage: {
      src: item.image,
      alt: item.title,
      width: 80,
      height: 80,
    },
    size: cartItemSelect.sizing(item),
    subscription:
      activePromo && cartValue && cartValue >= $`30`
        ? "Free with $30+ order"
        : subscriptionText(item),
  };

  const sellingPlan = sellingPlanAllocation?.selling_plan;
  let discountValue: number | null = null;

  // Derive product flags for bulk/bundle exclusions
  const droppsProduct = item.shopifyProduct;
  const isBulk = droppsProduct ? productSelect.isBulk(droppsProduct) : false;
  const isBundle = Boolean(
    droppsProduct &&
      (droppsProduct.type === "Bundle" ||
        droppsProduct.bundledItems?.length ||
        droppsProduct.tags?.includes("bundle"))
  );
  const isTrialProduct = droppsProduct?.tags?.includes("trial-product");

  if (
    hasCompareAtDiscount &&
    sellingPlan?.price_adjustments &&
    sellingPlan?.price_adjustments?.length > 0
  ) {
    if (discountedPrice) {
      discountValue = Math.round(
        ((compareAtLinePrice.asCents() - discountedPrice.asCents()) /
          compareAtLinePrice.asCents()) *
          100
      );
    } else {
      discountValue = sellingPlan.price_adjustments[0].value;
    }
  } else if (hasCustomDiscount && discountedPrice) {
    discountValue = Math.round(
      ((item.original_line_price.asCents() - discountedPrice.asCents()) /
        item.original_line_price.asCents()) *
        100
    );
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-start justify-between">
        <ProductCard
          compact
          hoverable={false}
          rounded={false}
          fullwidth
          productCardValue={cartItem}
          isTrialProduct={isTrialProduct}
          cover={
            cartItem.featuredImage?.src ? (
              <a href={productPageUrl} onClick={closeDrawer}>
                <div className="w-20 shrink-0 [&>img]:max-w-full">
                  <SizedImage
                    src={cartItem.featuredImage.src}
                    alt={cartItem.title}
                    height={80}
                    width={80}
                  />
                </div>
              </a>
            ) : undefined
          }
          productTitle={
            <a href={productPageUrl} onClick={closeDrawer}>
              <h3
                data-id="product-card-title"
                className="title text-sm transition hover:underline"
              >
                {productSelect.name(cartItem)}
              </h3>
            </a>
          }
        />
      </div>
      <div className="flex flex-col px-4 pb-4">
        <div className="flex items-center justify-between">
          <PlusMinus
            value={item.quantity}
            decrement={async () => {
              const newQuantity = Math.max(0, item.quantity - 1);

              if (newQuantity === 0) {
                // Only handle bundle removal if this item is a bundle item
                if (bundleProductIds.includes(item.product_id)) {
                  await handleBundleRemoval(item);
                } else {
                  // Normal item removal
                  await cartUpdater.mutateAsync({
                    id: item.key,
                    quantity: newQuantity,
                  });
                }
              } else {
                // Normal quantity update
                cartUpdater.mutate({ id: item.key, quantity: newQuantity });
              }
            }}
            increment={() => {
              const newQuantity = item.quantity + 1;
              cartUpdater.mutate({ id: item.key, quantity: newQuantity });
            }}
            enableTrash={true}
            disabled={isGwpItem}
            min={1}
          />
          <PriceDisplay
            standardPrice={
              hasDiscount && sellingPlanAllocation?.compare_at_price
                ? Money.from(sellingPlanAllocation.compare_at_price, "¢").mul(
                    item.quantity
                  )
                : displayStandardPrice
            }
            discountPrice={
              hasDiscount
                ? (() => {
                    // Use global discount utility for consistent pricing
                    if (
                      sellingPlan &&
                      sellingPlan.price_adjustments &&
                      sellingPlan.price_adjustments.length > 0
                    ) {
                      const priceAdjustment = sellingPlan.price_adjustments[0];
                      if (priceAdjustment.value_type === "percentage") {
                        const percent = getEffectiveSubscriptionDiscountPercent(
                          priceAdjustment.value,
                          { isBulk, isBundle }
                        );
                        // Calculate discounted unit price first, then multiply by quantity (consistent with cart subtotal)
                        const unitCompareAtPrice =
                          sellingPlanAllocation?.compare_at_price
                            ? Money.from(
                                sellingPlanAllocation.compare_at_price,
                                "¢"
                              )
                            : item.price;
                        const discountedUnitPrice = unitCompareAtPrice.mul(
                          1 - percent / 100
                        );
                        return discountedUnitPrice.mul(item.quantity);
                      }
                    }
                    // Fallback to backend price if no selling plan discount
                    return (
                      (item.discounted_price &&
                        item.discounted_price.mul(item.quantity)) ||
                      item.final_line_price
                    );
                  })()
                : undefined
            }
            showDiscountPrice={hasDiscount}
          />
        </div>
        {discountValue != null &&
          discountValue > 0 &&
          discountValue !== 100 && (
            <div className="self-end text-xs font-semibold text-secondary-700">
              {`${getEffectiveSubscriptionDiscountPercent(discountValue, {
                isBulk,
                isBundle,
              })}% OFF`}
            </div>
          )}
        {item.shopifyProduct?.bundledItems?.length && (
          <div
            role="button"
            tabIndex={0}
            className="flex cursor-pointer items-center pt-1 underline"
            onKeyDown={() =>
              setOpenDrawer({
                kind: "bundle-drawer",
                bundledProducts: item.shopifyProduct?.bundledItems ?? [],
                item,
              })
            }
            onClick={() => {
              setOpenDrawer({
                kind: "bundle-drawer",
                bundledProducts: item.shopifyProduct?.bundledItems ?? [],
                item,
              });
            }}
          >
            <p className="mb-0 text-sm">What&apos;s included</p>
            <KeyboardArrowDownIcon />
          </div>
        )}
      </div>
    </div>
  );
};
